import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import './AdminFormStyles.css';

interface CouponFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  coupon?: any | null;
}

const CouponFormModal: React.FC<CouponFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  coupon
}) => {
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase_amount: '',
    max_discount_amount: '',
    usage_limit: '',
    valid_from: '',
    valid_to: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (coupon) {
      setFormData({
        code: coupon.code || '',
        discount_type: coupon.discount_type || 'percentage',
        discount_value: coupon.discount_value || '',
        min_purchase_amount: coupon.min_purchase_amount || '',
        max_discount_amount: coupon.max_discount_amount || '',
        usage_limit: coupon.usage_limit || '',
        valid_from: coupon.valid_from ? coupon.valid_from.split('T')[0] : '',
        valid_to: coupon.valid_to ? coupon.valid_to.split('T')[0] : '',
        is_active: coupon.is_active ?? true
      });
    } else {
      setFormData({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_purchase_amount: '',
        max_discount_amount: '',
        usage_limit: '',
        valid_from: '',
        valid_to: '',
        is_active: true
      });
    }
    setErrors({});
  }, [coupon, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.code.trim()) newErrors.code = 'Coupon code is required';
    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      newErrors.discount_value = 'Discount value must be greater than 0';
    }
    if (formData.discount_type === 'percentage' && parseFloat(formData.discount_value) > 100) {
      newErrors.discount_value = 'Percentage cannot exceed 100%';
    }
    if (formData.valid_from && formData.valid_to && formData.valid_to <= formData.valid_from) {
      newErrors.valid_to = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        code: formData.code.toUpperCase(),
        min_purchase_amount: formData.min_purchase_amount || 0,
        max_discount_amount: formData.max_discount_amount || null,
        usage_limit: formData.usage_limit || null
      };
      await onSave(submitData);
      onClose();
    } catch (error: any) {
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={coupon ? 'Edit Coupon' : 'Add Coupon'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">
          {/* Coupon Code */}
          <div className="admin-form-row">
            <div className="admin-form-label">Coupon Code *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="e.g., SUMMER20"
                className={errors.code ? 'has-error' : ''}
                style={{ textTransform: 'uppercase' }}
              />
              {errors.code && <span className="error-text">{errors.code}</span>}
            </div>
          </div>

          {/* Discount Type */}
          <div className="admin-form-row">
            <div className="admin-form-label">Discount Type *</div>
            <div className="admin-form-input">
              <select
                name="discount_type"
                value={formData.discount_type}
                onChange={handleChange}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
          </div>

          {/* Discount Value */}
          <div className="admin-form-row">
            <div className="admin-form-label">
              Discount Value * {formData.discount_type === 'percentage' ? '(%)' : '(৳)'}
            </div>
            <div className="admin-form-input">
              <input
                type="number"
                name="discount_value"
                value={formData.discount_value}
                onChange={handleChange}
                step="0.01"
                min="0"
                className={errors.discount_value ? 'has-error' : ''}
              />
              {errors.discount_value && <span className="error-text">{errors.discount_value}</span>}
            </div>
          </div>

          {/* Min Purchase Amount */}
          <div className="admin-form-row">
            <div className="admin-form-label">Min Purchase (৳)</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="min_purchase_amount"
                value={formData.min_purchase_amount}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </div>
          </div>

          {/* Max Discount Amount */}
          <div className="admin-form-row">
            <div className="admin-form-label">Max Discount (৳)</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="max_discount_amount"
                value={formData.max_discount_amount}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </div>
          </div>

          {/* Usage Limit */}
          <div className="admin-form-row">
            <div className="admin-form-label">Usage Limit</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="usage_limit"
                value={formData.usage_limit}
                onChange={handleChange}
                min="0"
                placeholder="Unlimited"
              />
            </div>
          </div>

          {/* Valid From */}
          <div className="admin-form-row">
            <div className="admin-form-label">Valid From</div>
            <div className="admin-form-input">
              <input
                type="date"
                name="valid_from"
                value={formData.valid_from}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Valid To */}
          <div className="admin-form-row">
            <div className="admin-form-label">Valid To</div>
            <div className="admin-form-input">
              <input
                type="date"
                name="valid_to"
                value={formData.valid_to}
                onChange={handleChange}
                className={errors.valid_to ? 'has-error' : ''}
              />
              {errors.valid_to && <span className="error-text">{errors.valid_to}</span>}
            </div>
          </div>

          {/* Active Status */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Status</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>Active Coupon</span>
              </label>
            </div>
          </div>
        </div>

        <div className="admin-form-actions">
          <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CouponFormModal;
